import React from 'react';
import PropTypes from 'prop-types';
import { poolMemberKey } from '../utils/buildPoolEngineInput';

import { OVERSUBSCRIBED_LABEL, OVERSUBSCRIBED_BODY, UNALLOCATED_BODY } from '../../../shared/poolOversubscribed';
import { describeCapOverflow, hasCapOverflow } from '../../../shared/poolCapOverflow';
import { describePoolShareOfGross, describeTakeShares } from '../../../shared/poolShareOfGross';
import { formatCurrencySymbol } from '../../../shared/teamCurrencyFormatting';

// O-2783 "pool over offers". Live pool-split preview: the pool total and each
// member's computed take, driven by FinancialSummaryMath's `poolMembers` output
// (keyed by performerId) + `artistEarnings.poolAmount`. Read-only — the numbers
// recompute from the engine as the pool deal / member shares change.

const formatAmountIn = (currency) => (n) => {
  const symbol = formatCurrencySymbol(currency);
  const v = Number(n);
  if (!Number.isFinite(v)) return `${symbol}0`;
  // Whole dollars when it is a whole number, cents when it is not — a $975.50
  // shortfall rendered as "$976" is not a figure anyone can reconcile against.
  const fractional = Math.round(v * 100) % 100 !== 0;
  return `${symbol}${v.toLocaleString('en-US', fractional
    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    : { maximumFractionDigits: 0 })}`;
};

const V3PoolSplitPreview = ({
  poolMembers, poolAmount, grossPotential, dealBracketReached, oversubscribed, unallocated, lineup, team,
}) => {
  if (!poolMembers || poolMembers.length === 0) return null;

  const formatAmount = formatAmountIn(team?.base_currency_symbol);

  const earnings = { poolAmount, grossPotential, dealBracketReached };
  const poolShareOfGrossNote = describePoolShareOfGross(earnings, { formatAmount });

  // Map performerId → take. The preview engine keys members on performer_id,
  // falling back to the lineup row id; build the same fallback key here so the
  // member name lines up with its take.
  const takeByKey = new Map(poolMembers.map((m) => [m.performerId, m]));

  const nameByKey = new Map((lineup || []).map((member) => [poolMemberKey(member), member.performer_name]));

  const rows = (lineup || []).map((member) => {
    const key = poolMemberKey(member);
    const result = takeByKey.get(key);
    return {
      id: member.id,
      name: member.performer_name || 'Unnamed artist',
      take: result ? result.take : 0,
      shares: result ? describeTakeShares(result.take, earnings) : null,
      capped: !!(result && result.capped),
      floored: !!(result && result.floored),
      capOverflowNote: result && result.capped && hasCapOverflow(result)
        ? describeCapOverflow(result, { nameFor: (performerId) => nameByKey.get(performerId), formatAmount })
        : null,
    };
  });

  return (
    <section className="deal-terms-card-v2 pool-split-preview" data-testid="v3-pool-split-preview">
      <div className="deal-terms-card-v2__title-block" style={{ padding: '12px 16px 0' }}>
        <span className="deal-terms-card-v2__title">Pool split</span>
        <span className="deal-terms-card-v2__subtitle">
          Pool total <strong>{formatAmount(poolAmount)}</strong>
          {poolShareOfGrossNote && (
            <span data-testid="pool-split-preview-gross">{` = ${poolShareOfGrossNote}`}</span>
          )}
        </span>
      </div>

      {oversubscribed && (
        <div
          className="pool-split-preview__warning"
          data-testid="pool-split-preview-warning"
          style={{
            margin: '8px 16px 0',
            padding: '8px 10px',
            background: '#FEF3C7',
            border: '1px solid #FCD34D',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#92400E',
          }}
        >
          <strong>{OVERSUBSCRIBED_LABEL}</strong> — {OVERSUBSCRIBED_BODY}
        </div>
      )}

      {/* The counterpart to the oversubscribed banner: money the pool holds that
        * nobody was paid. Without it a 60/30 typo, or a versus member who took
        * their guarantee and forfeited their share, previews as a normal split
        * and the artist is quietly short. */}
      {!oversubscribed && unallocated > 0 && (
        <div
          className="pool-split-preview__unallocated"
          data-testid="pool-split-preview-unallocated"
          style={{
            margin: '8px 16px 0', padding: '8px 10px', background: 'var(--od-c-f3f4f6, #F3F4F6)',
            border: '1px solid var(--od-c-e5e7eb, #E5E7EB)', borderRadius: '6px', fontSize: '12px', color: 'var(--od-c-4b5563, #4B5563)',
          }}
        >
          <strong>{formatAmount(unallocated)} unallocated</strong> — {UNALLOCATED_BODY}
        </div>
      )}

      <div style={{ padding: '8px 16px 12px' }}>
        {rows.map((row) => (
          <div key={row.id} className="pool-split-preview__row" data-testid="pool-split-preview-row">
            <span className="pool-split-preview__name">{row.name}</span>
            <span className="pool-split-preview__take">
              {formatAmount(row.take)}
              {row.capped && (
                <span className="pool-split-preview__flag" style={{ marginLeft: '6px', fontSize: '11px', color: '#B45309' }}>
                  capped
                </span>
              )}
              {row.floored && (
                <span className="pool-split-preview__flag" style={{ marginLeft: '6px', fontSize: '11px', color: '#047857' }}>
                  floored
                </span>
              )}
            </span>
            {row.shares && (
              <div className="pool-split-preview__shares" data-testid="pool-split-preview-shares">{row.shares}</div>
            )}
            {row.capOverflowNote && (
              <div className="pool-split-preview__overflow" data-testid="pool-split-preview-overflow">
                Cap overflow: {row.capOverflowNote}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

V3PoolSplitPreview.propTypes = {
  poolMembers: PropTypes.arrayOf(PropTypes.object),
  poolAmount: PropTypes.number,
  grossPotential: PropTypes.number,
  dealBracketReached: PropTypes.shape({ number: PropTypes.number, bracketCount: PropTypes.number }),
  oversubscribed: PropTypes.bool,
  unallocated: PropTypes.number,
  lineup: PropTypes.arrayOf(PropTypes.object),
  team: PropTypes.object,
};

V3PoolSplitPreview.defaultProps = {
  poolMembers: [],
  poolAmount: 0,
  grossPotential: 0,
  dealBracketReached: null,
  oversubscribed: false,
  unallocated: 0,
  lineup: [],
  team: null,
};

export default V3PoolSplitPreview;
